import { useId, useState } from 'react';
import {
  NEGOTIABLE_TERMS,
  REQUEST_REASON_MAX_CHARS,
  REQUEST_REASON_MIN_CHARS,
  RequestedTermsSchema,
  validateRequestReason,
  type PaymentTermsDays,
} from '@hts/shared';
import type { FormData } from '../types';

// MUL-2333 (2026-09-08): HTS's default is paid-when-paid. The select shows
// "Paid when paid — standard." alongside the negotiable Net terms; picking it
// (or not selecting anything) sets `paymentTermsDays` to `null`. Net 45 was
// removed 09-08 and restored 2026-10-08 (Morgan, MUL-3919) — the options are
// driven by NEGOTIABLE_TERMS.paymentTermsDays.allowed.
//
// MUL-3919 (2026-09-22): the wizard now uses a radio fieldset (legend
// "Payment terms"), each option a 44px+ card. When `paymentTermsNegotiable`
// is false, the wizard skips this step entirely — see
// `activeStepTitles` in types.ts. The component still guards against
// rendering in the ineligible case so it remains safe even if a stale
// caller forgets to skip it.

interface Props {
  data: FormData;
  onChange: (updates: Partial<FormData>) => void;
  errors?: Record<string, string>;
  /** MUL-3919 §2: eligibility is resolved server-side. When false, render nothing. */
  paymentTermsNegotiable: boolean;
}

const REQUIRES_REVIEW: ReadonlySet<PaymentTermsDays> = new Set(NEGOTIABLE_TERMS.paymentTermsDays.allowed);

export function TermsStep({ data, onChange, errors = {}, paymentTermsNegotiable }: Props) {
  // MUL-3919 §6.1: skip the entire step when the sub isn't eligible. The
  // wizard's stepper already omits this step in that case; this guard is
  // belt-and-braces for callers that still mount the component.
  if (!paymentTermsNegotiable) {
    return null;
  }

  const fieldsetId = useId();
  const reasonId = useId();
  const reasonCounterId = useId();
  const [schemaError, setSchemaError] = useState<string | null>(null);

  const current = data.requestedTerms?.paymentTermsDays ?? null;
  const requiresReview = REQUIRES_REVIEW.has(current);

  const validateSelection = () => {
    const result = RequestedTermsSchema.safeParse(data.requestedTerms);
    setSchemaError(result.success ? null : 'Choose one of the available payment terms.');
  };

  const updateSelection = (paymentTermsDays: PaymentTermsDays) => {
    onChange({
      requestedTerms: { ...data.requestedTerms, paymentTermsDays },
      // Reset the reason when the sub returns to standard; clear local errors
      // immediately so the counter and validation message disappear too.
      ...(paymentTermsDays === null ? { requestReason: '' } : {}),
    });
    setSchemaError(null);
  };

  const updateReason = (value: string) => {
    onChange({ requestReason: value });
  };

  const errorMessage = schemaError || errors.paymentTermsDays;

  const reasonValidation = requiresReview
    ? validateRequestReason(data.requestReason ?? '')
    : { ok: true, trimmed: '' };
  const reasonLength = (data.requestReason ?? '').length;
  const reasonError =
    errors.requestReason || (requiresReview && !reasonValidation.ok ? reasonValidation.message : null);

  return (
    <div>
      <div className="step-header">
        <h2>Agreement Terms</h2>
        <p>HTS's default is paid when paid. Pick Net 30, Net 45 or Net 60 only when your situation requires it.</p>
      </div>

      <fieldset className="terms-fieldset" aria-describedby={`${fieldsetId}-help`}>
        <legend className="terms-legend">Payment terms <span className="required" aria-hidden="true">*</span></legend>

        <div id={`${fieldsetId}-help`} className="terms-help">
          HTS pays subcontractors after HTS receives payment from the customer. Net 30, Net 45 and Net 60 are situational overrides that an HTS approver must sign off on.
        </div>

        {/* Standard: paid when paid */}
        <label className={`terms-option${!requiresReview ? ' is-selected' : ''}`}>
          <input
            type="radio"
            name="paymentTermsDays"
            value="standard"
            checked={!requiresReview}
            onChange={() => updateSelection(null)}
            onBlur={validateSelection}
            className="terms-radio"
          />
          <span className="terms-option-body">
            <span className="terms-option-title">Paid when paid — standard.</span>
            <span className="terms-option-help">We pay you when our customer pays us for the work.</span>
          </span>
        </label>

        {/* Negotiable overrides — server re-validates eligibility */}
        {NEGOTIABLE_TERMS.paymentTermsDays.allowed.map((days) => (
          <label
            key={days}
            className={`terms-option${current === days ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name="paymentTermsDays"
              value={String(days)}
              checked={current === days}
              onChange={() => updateSelection(days)}
              onBlur={validateSelection}
              className="terms-radio"
            />
            <span className="terms-option-body">
              <span className="terms-option-title">Net {days} after customer payment — requires review.</span>
              <span className="terms-option-help">
                Choose this only if paid when paid won't work for your company. HTS will review your request.
              </span>
            </span>
          </label>
        ))}

        {errorMessage && (
          <p role="alert" className="error-text" style={{ marginTop: 12 }}>
            {errorMessage}
          </p>
        )}
      </fieldset>

      {requiresReview && (
        <div className="terms-reason-block" aria-live="polite">
          <label htmlFor={reasonId} className="terms-reason-label">
            Why are you requesting different terms? <span className="required" aria-hidden="true">*</span>
          </label>
          <textarea
            id={reasonId}
            rows={4}
            value={data.requestReason ?? ''}
            onChange={(event) => updateReason(event.target.value)}
            aria-describedby={`${reasonCounterId} ${reasonId}-help`}
            aria-invalid={Boolean(reasonError)}
            maxLength={REQUEST_REASON_MAX_CHARS + 50 /* allow mild overflow so the error can show */}
          />
          <p id={`${reasonId}-help`} className="alert alert-info" style={{ marginTop: 8 }}>
            This is a request. HTS must approve it before we prepare your agreement.
          </p>
          <p id={reasonCounterId} className="terms-reason-counter">
            {reasonLength} / {REQUEST_REASON_MAX_CHARS} characters
            {reasonLength < REQUEST_REASON_MIN_CHARS && reasonLength > 0
              ? ` (at least ${REQUEST_REASON_MIN_CHARS})`
              : ''}
          </p>
          {reasonError && (
            <p role="alert" className="error-text" style={{ marginTop: 4 }}>
              {reasonError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}